import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingIcon } from "../../assets/icons";
import { getMyNonFinancialRecords, getNonFinancialUploadLog, getNonFinancialReasons } from "../../service/query/auth";
import FilterDropdown from "./FilterDropdown";
import DateRangeFilter from "./DateRangeFilter";

const statusStyles = {
    PENDING: "bg-[#F3F4F6] text-[#6B7280]",
    PROCESSING: "bg-[#EDF6FF] text-[#0057FF]",
    SUCCESSFUL: "bg-[#E7F7EF] text-[#0E9F6E]",
    FAILED: "bg-[#FDECEC] text-[#E02424]",
};

const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center gap-1 px-[10px] py-[3px] rounded-full text-[12px] font-medium ${statusStyles[status] ?? "bg-[#F3F4F6] text-[#6B7280]"}`}>
        <span className="w-[6px] h-[6px] rounded-full bg-current" />
        {status}
    </span>
);

const columns = [
    "Bill Number", "Membership ID", "Points", "Amount", "Transaction date", "Transaction type",
    "Reason", "Transaction ID", "Source account", "Transaction channel", "Processing Status",
    "Response code", "Response message", "Uploaded by", "Processed at", "Processing Triggered",
];

export default function PointsAwardTransactions() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("mine");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [reason, setReason] = useState("");
    const [reasons, setReasons] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);

    useEffect(() => {
        const fetchReasons = async () => {
            try {
                const res = await getNonFinancialReasons();
                // console.log("reasons response:", res?.data);
                const data = res?.data?.data ?? [];
                setReasons(data);
            } catch (err) {
                console.error("Failed to fetch reasons:", err);
            }
        };
        fetchReasons();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                membershipId: search || undefined,
                status: status || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                reason: reason || undefined,
                page,
                perPage: 50,
            };
            const res = activeTab === "mine"
                ? await getMyNonFinancialRecords(params)
                : await getNonFinancialUploadLog(params);
            setRows(res?.data?.data ?? []);
            setPagination(res?.data?.pagination ?? null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, page]);

    const clearFilters = () => {
        setSearch("");
        setStatus("");
        setReason("");
        setDateFrom("");
        setDateTo("");
    };

    return (
        <div className="pt-6 bg-white min-h-[80vh]">
            <div className="flex justify-between items-center mb-[29px]">
                <button
                    onClick={() => navigate("/points-award/upload")}
                    className="bg-[#00305E] text-white rounded-[5px] h-[38px] px-8 text-[14px]"
                >
                    Upload file
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-[32px] border-b border-gray-200 mb-[24px]">
                <button
                    onClick={() => { setActiveTab("mine"); setPage(1); }}
                    className={`pb-3 text-sm transition-all ${activeTab === "mine" ? "border-b-2 border-blue-900 text-blue-900 font-medium" : "text-gray-400 hover:text-gray-600"}`}
                >
                    My uploaded transactions
                </button>
                <button
                    onClick={() => { setActiveTab("all"); setPage(1); }}
                    className={`pb-3 text-sm transition-all ${activeTab === "all" ? "border-b-2 border-blue-900 text-blue-900 font-medium" : "text-gray-400 hover:text-gray-600"}`}
                >
                    Uploaded transactions
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center mb-[24px]">
                <div className="flex items-center gap-2 border border-[#D0D5DD] rounded-full px-4 py-2 w-[280px]">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="#98A2B3" strokeWidth="1.5" />
                        <path d="M21 21L16.65 16.65" stroke="#98A2B3" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search membership ID"
                        className="text-sm outline-none w-full text-[#344054] placeholder:text-[#98A2B3]"
                    />
                </div>

                <FilterDropdown
                    label="Reason"
                    value={reason}
                    onChange={setReason}
                    options={[
                        { value: "", label: "All" },
                        ...reasons.map((r) => ({ value: r, label: r })),
                    ]}
                />

                <FilterDropdown
                    label="Status"
                    value={status}
                    onChange={setStatus}
                    options={[
                        { value: "", label: "All" },
                        { value: "pending", label: "Pending" },
                        { value: "processing", label: "Processing" },
                        { value: "successful", label: "Successful" },
                        { value: "failed", label: "Failed" },
                        { value: "unprocessed", label: "Unprocessed" },
                    ]}
                />

                <DateRangeFilter
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onChange={({ dateFrom, dateTo }) => {
                        setDateFrom(dateFrom);
                        setDateTo(dateTo);
                    }}
                />

                <button
                    onClick={() => { setPage(1); fetchData(); }}
                    className="border rounded px-4 py-2 text-sm"
                >
                    Show results
                </button>
                <button
                    onClick={clearFilters}
                    className="text-sm flex items-center gap-1 text-[#344054]"
                >
                    ✕ Clear filters
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <LoadingIcon />
                </div>
            ) : (
                <div className="rounded-md overflow-hidden">
                    <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
                        <table className="w-full text-sm text-left min-w-[1600px]">
                            <thead className="bg-[#F8FAFC] sticky top-0 h-[44px]">
                                <tr>
                                    {columns.map((h) => (
                                        <th key={h} className="px-[24px] py-[12px] text-black text-[12px] font-semibold border-b border-[#E9EAEB] whitespace-nowrap">
                                            {h} ↓
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center py-10 text-[#6D6D6D] text-[14px]">
                                            No transactions found
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.id} className="border-b hover:bg-blue-50 transition-colors">
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.bill_no ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.membership_id}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.points}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.amount}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.transaction_date}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.transaction_type}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.reason ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.transaction_id ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.source_account ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.transaction_channel ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[14px] whitespace-nowrap">
                                                <StatusBadge status={row.processing_status} />
                                            </td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.response_code ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.response_message ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.uploaded_by?.name ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[#64748B] text-[14px] whitespace-nowrap">{row.processed_at ?? "-"}</td>
                                            <td className="px-[24px] py-[16px] text-[14px] whitespace-nowrap">
                                                {row.upload?.processing_trigger
                                                    ? <span className="text-[#0E9F6E] font-medium">Yes</span>
                                                    : <span className="text-[#E02424] font-medium">No</span>
                                                }
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pagination && (
                        <div className="flex items-center justify-between mt-4 px-2">
                            <p className="text-[12px] text-[#64748B]">
                                Page {pagination.current_page} of {pagination.last_page} — {pagination.total} records
                            </p>
                            {pagination.last_page > 1 && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={pagination.current_page === 1}
                                        className={`px-3 py-1 rounded text-[12px] border transition-all ${pagination.current_page === 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-[#00305E] text-[#00305E] hover:bg-blue-50"}`}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                                        disabled={pagination.current_page === pagination.last_page}
                                        className={`px-3 py-1 rounded text-[12px] border transition-all ${pagination.current_page === pagination.last_page ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-[#00305E] text-[#00305E] hover:bg-blue-50"}`}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}